import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { cn } from '../../../../lib/utils';
import { smallTextSize, tertiaryText } from '../../../../styling/stylingHelper';
import { getColorForIndex } from '../../../../utils/chartColors';
import { DonutSlice } from '../../../../services/admin/cloudAgentTypes';

ChartJS.register(ArcElement, Tooltip, Legend);

export interface DonutChartProps {
  slices: DonutSlice[];
  emptyText?: string;
  heightClass?: string;
  legendMaxWidthClass?: string;
}

/**
 * Dumb donut chart (data via props). Visual model taken from
 * `components/orderhistory/SpendByCategoryDonut.tsx` but it recomputes NOTHING — it
 * renders the hydrated `slices` (label/value/percentage/color) verbatim. Colors fall
 * back to the shared palette when the hydrator left them empty.
 */
export const DonutChart: React.FC<DonutChartProps> = ({
  slices,
  emptyText,
  heightClass = 'h-[200px] sm:h-[240px]',
  legendMaxWidthClass = 'max-w-[424px]',
}) => {
  const colored = useMemo(
    () => slices.map((s, i) => ({ ...s, color: s.color || getColorForIndex(i) })),
    [slices],
  );

  const data: ChartData<'doughnut'> = useMemo(
    () => ({
      labels: colored.map((s) => s.label),
      datasets: [
        {
          data: colored.map((s) => s.value),
          backgroundColor: colored.map((s) => s.color),
          borderWidth: 0,
        },
      ],
    }),
    [colored],
  );

  const options: ChartOptions<'doughnut'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: '62%',
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const slice = colored[ctx.dataIndex];
              return slice ? `${slice.label}: ${slice.percentage}%` : '';
            },
          },
        },
      },
    }),
    [colored],
  );

  if (colored.length === 0) {
    return (
      <div className="flex items-center justify-center w-full py-8">
        <p className={cn(smallTextSize, tertiaryText)}>{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 w-full">
      <div className={cn('relative w-full sm:w-1/2', heightClass)}>
        <Doughnut data={data} options={options} />
      </div>
      <div className={cn('flex flex-col gap-2 w-full sm:w-1/2 overflow-y-auto custom-scroll', legendMaxWidthClass)}>
        {colored.map((s) => (
          <div key={s.label} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-medium truncate">
              {s.label}
            </span>
            <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 ml-auto">
              {s.percentage}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
